import { supabase } from '@/integrations/supabase/client';

export interface Debate {
  id: string;
  interview_id: string;
  creator_user_id: string;
  invited_user_id?: string;
  invited_username?: string;
  assertion: string;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
  creator_profile?: {
    display_name: string;
    avatar_url?: string;
  };
  invited_profile?: {
    display_name: string;
    avatar_url?: string;
  };
  arguments?: DebateArgument[];
  votes?: DebateVote[];
}

export interface DebateArgument {
  id: string;
  debate_id: string;
  user_id: string;
  argument_number: number;
  argument_text: string;
  created_at: string;
  profiles?: {
    display_name: string;
    avatar_url?: string;
  };
}

export interface DebateVote {
  id: string;
  debate_id: string;
  voter_user_id: string;
  voted_for_user_id: string;
  created_at: string;
}

export interface CreateDebateData {
  interview_id: string;
  assertion: string;
  invited_username?: string;
}

export class DebatesService {
  static async getDebatesForInterview(interviewId: string): Promise<Debate[]> {
    const { data, error } = await supabase
      .from('debates')
      .select(`
        *,
        creator_profile:profiles!debates_creator_user_id_fkey (
          display_name,
          avatar_url
        ),
        invited_profile:profiles!debates_invited_user_id_fkey (
          display_name,
          avatar_url
        )
      `)
      .eq('interview_id', interviewId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching debates:', error);
      throw error;
    }

    // Fetch arguments and votes for each debate
    const debatesWithDetails = await Promise.all(
      (data || []).map(async (debate) => {
        const [debateArguments, votes] = await Promise.all([
          this.getDebateArguments(debate.id),
          this.getDebateVotes(debate.id),
        ]);
        return { 
          ...debate, 
          arguments: debateArguments, 
          votes,
          creator_profile: debate.creator_profile || { display_name: 'Unknown', avatar_url: undefined },
          invited_profile: debate.invited_profile || { display_name: 'Unknown', avatar_url: undefined }
        } as Debate;
      })
    );

    return debatesWithDetails;
  }

  static async getDebateArguments(debateId: string): Promise<DebateArgument[]> {
    const { data, error } = await supabase
      .from('debate_arguments')
      .select(`
        *,
        profiles!debate_arguments_user_id_fkey (
          display_name,
          avatar_url
        )
      `)
      .eq('debate_id', debateId)
      .order('argument_number', { ascending: true });

    if (error) {
      console.error('Error fetching debate arguments:', error);
      return [];
    }

    return (data || []).map(arg => ({
      ...arg,
      profiles: arg.profiles || { display_name: 'Unknown', avatar_url: undefined }
    })) as DebateArgument[];
  }

  static async getDebateVotes(debateId: string): Promise<DebateVote[]> {
    const { data, error } = await supabase
      .from('debate_votes')
      .select('*')
      .eq('debate_id', debateId);

    if (error) {
      console.error('Error fetching debate votes:', error);
      return [];
    }

    return data || [];
  }

  static async createDebate(debateData: CreateDebateData): Promise<Debate | null> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be authenticated to create debates');
    }

    // Find invited user if username provided
    let invitedUserId = null;
    if (debateData.invited_username) {
      const { data: invitedUser } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('username', debateData.invited_username)
        .single();
      
      invitedUserId = invitedUser?.user_id;
    }

    const { data, error } = await supabase
      .from('debates')
      .insert({
        interview_id: debateData.interview_id,
        creator_user_id: user.id,
        invited_user_id: invitedUserId,
        invited_username: debateData.invited_username,
        assertion: debateData.assertion,
        status: invitedUserId ? 'pending' : 'active',
      })
      .select(`
        *,
        creator_profile:profiles!debates_creator_user_id_fkey (
          display_name,
          avatar_url
        ),
        invited_profile:profiles!debates_invited_user_id_fkey (
          display_name,
          avatar_url
        )
      `)
      .single();

    if (error) {
      console.error('Error creating debate:', error);
      throw error;
    }

    return {
      ...data,
      creator_profile: data?.creator_profile || { display_name: 'Unknown', avatar_url: undefined },
      invited_profile: data?.invited_profile || { display_name: 'Unknown', avatar_url: undefined }
    } as Debate;
  }

  static async addArgument(debateId: string, argumentText: string): Promise<DebateArgument | null> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be authenticated to add arguments');
    }

    // Get current argument count for numbering
    const { data: existingArgs } = await supabase
      .from('debate_arguments')
      .select('argument_number')
      .eq('debate_id', debateId)
      .eq('user_id', user.id)
      .order('argument_number', { ascending: false })
      .limit(1);

    const nextArgumentNumber = existingArgs && existingArgs.length > 0 
      ? existingArgs[0].argument_number + 1 
      : 1;

    const { data, error } = await supabase
      .from('debate_arguments')
      .insert({
        debate_id: debateId,
        user_id: user.id,
        argument_number: nextArgumentNumber,
        argument_text: argumentText,
      })
      .select(`
        *,
        profiles!debate_arguments_user_id_fkey (
          display_name,
          avatar_url
        )
      `)
      .single();

    if (error) {
      console.error('Error adding argument:', error);
      throw error;
    }

    return {
      ...data,
      profiles: data?.profiles || { display_name: 'Unknown', avatar_url: undefined }
    } as DebateArgument;
  }

  static async voteForDebate(debateId: string, votedForUserId: string): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be authenticated to vote');
    }

    // Remove existing vote if any
    await supabase
      .from('debate_votes')
      .delete()
      .eq('debate_id', debateId)
      .eq('voter_user_id', user.id);

    // Add new vote
    const { error } = await supabase
      .from('debate_votes')
      .insert({
        debate_id: debateId,
        voter_user_id: user.id,
        voted_for_user_id: votedForUserId,
      });

    if (error) {
      console.error('Error voting:', error);
      throw error;
    }

    return true;
  }

  static async updateDebateStatus(debateId: string, status: Debate['status']): Promise<boolean> {
    const { error } = await supabase
      .from('debates')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', debateId);

    if (error) {
      console.error('Error updating debate status:', error);
      throw error;
    }

    return true;
  }
}